/**
 * URL helpers (RULES §UI-3: the URL is the state). Global params live on the root route:
 *   ?p=<id>,<id>        project scope (absent = all projects)
 *   ?range=7d           today | 7d | 30d | month | custom
 *   ?from=&to=          YYYY-MM-DD, inclusive, when range=custom
 */
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { isIsoDay, isRangeKey, type RangeKey } from './timeRange';

export interface GlobalSearch {
  p?: string;
  range?: RangeKey;
  from?: string;
  to?: string;
}

export const GLOBAL_KEYS = ['p', 'range', 'from', 'to'] as const;

export function str(v: unknown): string | undefined {
  if (typeof v === 'number') return String(v);
  return typeof v === 'string' && v.trim() !== '' ? v : undefined;
}

export function list(v: string | undefined): string[] {
  return v
    ? v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
}

export function joinList(ids: readonly string[]): string | undefined {
  return ids.length ? ids.join(',') : undefined;
}

export function validateGlobalSearch(search: Record<string, unknown>): GlobalSearch {
  return {
    p: str(search.p),
    range: isRangeKey(search.range) ? search.range : undefined,
    from: isIsoDay(search.from) ? search.from : undefined,
    to: isIsoDay(search.to) ? search.to : undefined,
  };
}

function clean(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== '' && v !== null));
}

/** Merge a patch into the current URL's search params (untyped; for cross-route params). */
export function useUpdateSearch(): (patch: Record<string, unknown>, opts?: { replace?: boolean }) => void {
  const navigate = useNavigate();
  return useCallback(
    (patch, opts) => {
      void navigate({
        to: '.',
        search: ((prev: Record<string, unknown>) => clean({ ...prev, ...patch })) as never,
        replace: opts?.replace,
      });
    },
    [navigate],
  );
}

/** A concrete app path for <Link> / navigate where the route is not known statically. */
export function pathTo(path: string): { to: '/' } {
  return { to: path as '/' };
}

/** Paths the assistant may navigate to: same-origin app paths only. */
export function isSafeAppPath(path: unknown): path is string {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\') && !/^\/*[a-z]+:/i.test(path);
}
